/**
 * Animated loading state over CSV export modal while server prepares file.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { motion } from 'framer-motion';

import { LoadingSpinner } from '@/components/ui';

import { formatServerCsvExportDurationHint } from '../formatServerCsvExportDurationHint';

export interface CsvExportLoadingOverlayProps {
  /** Voluum totalRows when known (phone tab “All”); drives minute estimate. */
  serverExportEstimatedRowCount?: number | null;
}

export const CsvExportLoadingOverlay: FC<CsvExportLoadingOverlayProps> = ({
  serverExportEstimatedRowCount,
}) => {
  const { minutesLine, whyLine } =
    formatServerCsvExportDurationHint(serverExportEstimatedRowCount);

  return (
  <motion.div
    className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4 rounded-lg bg-background/92 px-8 backdrop-blur-md"
    role="status"
    aria-live="polite"
    aria-busy="true"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.2 }}
  >
    <motion.div
      animate={{ scale: [1, 1.06, 1], opacity: [0.85, 1, 0.85] }}
      transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
    >
      <LoadingSpinner size="lg" />
    </motion.div>
    <div className="max-w-md space-y-3 text-center">
      <p className="text-sm font-semibold text-foreground">Preparing your export…</p>
      <p className="text-sm font-medium text-foreground">{minutesLine}</p>
      <p className="text-xs leading-relaxed text-muted-foreground">{whyLine}</p>
      <p className="text-xs text-muted-foreground/90">Please keep this window open until the download starts.</p>
    </div>
  </motion.div>
  );
};
